import React, { Component } from 'react'
import PropTypes, { instanceOf } from 'prop-types'
import querystring from 'query-string'
import { withCookies, Cookies } from 'react-cookie'
import { connect } from 'react-redux'
import config from '@adc/configment'
import logger from '@adc/logger'
import {
  actions,
  Semantic,
  LogoImage,
  Form,
  InputField,
} from '@adc/ui-morpheus'
import classnames from 'classnames/bind'
import { authenticateUser } from '../../../client/providers/user'
import { LOGIN_EMAIL } from '../../constants/cookieKeys'
import {
  LOGIN_ERROR_HEADER,
  LOGIN_INVALID_EMAIL,
  LOGIN_INCORRECT_CREDS,
  LOGIN_AUCTIONCOM_EMAIL,
} from '../../constants/errorMessages'
import {
  FORGOT_PASSWORD,
  REGISTER_ACCOUNT,
  CONTACT_SUPPORT_LOGIN,
} from '../../constants/externalLinks'
import { isValidEmail } from '../../util/validators'
import styles from '../../scss/login.scss'
import logo from '../../images/morpheus_logo_on_dark.svg'
import oktaLogo from './okta.png'
const cx = classnames.bind(styles)

const { List, Segment, Message, Transition, Divider, Header, Image } = Semantic

const WHITELISTED_PROD_EMAILS = ['morph.auto@redcmail.com']
class LoginContainer extends Component {
  static propTypes = {
    updateAuthenticatedUser: PropTypes.func.isRequired,
    cookies: instanceOf(Cookies).isRequired,
  }

  state = {
    email: '',
    password: '',
    error: '',
    isMounted: false,
    isProcessing: false,
    rememberEmail: false,
  }

  formFieldProps = key => {
    return {
      name: key,
      value: this.state[key],
      onChange: e => {
        this.setState({ [key]: e.target.value })
      },
    }
  }

  authenticate = e => {
    e.preventDefault()
    this.setState({ isProcessing: true, error: '' })

    const { cookies, updateAuthenticatedUser } = this.props
    const { email, password, rememberEmail } = this.state
    if (!isValidEmail(email)) {
      setTimeout(() => {
        this.setState({
          error: LOGIN_INVALID_EMAIL,
          isProcessing: false,
        })
      }, 100) // delay to allow renderError animation
      return
    }

    if (
      config.get('ENV') === 'prod' &&
      !WHITELISTED_PROD_EMAILS.includes(email)
    ) {
      setTimeout(() => {
        this.setState({
          error: LOGIN_AUCTIONCOM_EMAIL,
          isProcessing: false,
        })
      }, 100)

      return
    }

    authenticateUser(email, password)
      .then(user => {
        if (rememberEmail) {
          cookies.set(LOGIN_EMAIL, email, { path: '/' })
        } else {
          cookies.remove(LOGIN_EMAIL)
        }

        return updateAuthenticatedUser(user)
      })
      .catch(error => {
        logger.error('Login Error', error)

        this.setState({
          isProcessing: false,
          error: error.error || LOGIN_INCORRECT_CREDS,
        })
      })
  }

  toggleRememberEmail = e => {
    this.setState({ rememberEmail: !this.state.rememberEmail })
  }

  renderLinks = () => (
    <List relaxed size='tiny' className={cx('bold')}>
      <List.Item>
        <Divider hidden fitted />
      </List.Item>
      <List.Item
        as='a'
        href={`${config.get('AUCTIONCOM_URL')}${FORGOT_PASSWORD}`}
        data-elm-id='forgot_password'
      >
        Forgot Password?
      </List.Item>
      <List.Item
        as='a'
        href={`${config.get('AUCTIONCOM_URL')}${REGISTER_ACCOUNT}`}
        data-elm-id='register_account'
      >
        Register a New Account
      </List.Item>
      <List.Item
        as='a'
        href={CONTACT_SUPPORT_LOGIN}
        data-elm-id='contact_support'
      >
        Contact Support
      </List.Item>
    </List>
  )

  oktaRedirect = e => {
    e.preventDefault()
    if (typeof window === 'undefined' || !window.location) return null

    const isEmbed = window.location.pathname.startsWith('/embed/')
    if (isEmbed) {
      // Open popup for Okta auth — iframe can't frame Okta's login page.
      // After auth, popup generates an auth code and postMessages it back.
      const returnTo = encodeURIComponent(
        `${window.location.pathname}${window.location.search || ''}`
      )
      window.open(
        `/embed/auth/start?returnTo=${returnTo}`,
        'morpheus-embed-auth',
        'popup,width=500,height=650'
      )
    } else {
      window.location = '/authorization-code/callback'
    }
    return null
  }

  renderOktaButton = () => (
    <>
      <div className={cx('or')}>or</div>
      <Form.Group>
        <Form.Button
          onClick={this.oktaRedirect}
          size='large'
          fluid
          basic
          color='blue'
          width={16}
          data-elm-id='okta'
        >
          <Image src={oktaLogo} className={cx('oktalogo')} />
          Sign In With Okta
        </Form.Button>
      </Form.Group>
    </>
  )

  renderForm = () => {
    const { email, password, isMounted, isProcessing, rememberEmail } =
      this.state
    return (
      <Form
        data-elm-id='login_form'
        onSubmit={this.authenticate}
        padded={false}
      >
        <Segment attached='top' style={{ paddingLeft: 0, paddingRight: 0 }}>
          <InputField
            elmId='email'
            type='text'
            size='large'
            icon='user'
            iconPosition='left'
            placeholder='Email'
            transparent
            disabled={!isMounted}
            className='compact'
            {...this.formFieldProps('email')}
          />
        </Segment>
        <Segment attached='bottom' style={{ paddingLeft: 0, paddingRight: 0 }}>
          <InputField
            elmId='password'
            type='password'
            size='large'
            icon='lock'
            iconPosition='left'
            placeholder='Password'
            transparent
            disabled={!isMounted}
            className='compact'
            autoComplete='off'
            {...this.formFieldProps('password')}
          />
        </Segment>
        <Form.Checkbox
          checked={rememberEmail}
          label='Remember my email'
          onClick={this.toggleRememberEmail}
          data-elm-id='remember_email'
        />
        <Form.Group>
          <Form.Button
            type='submit'
            size='large'
            fluid
            primary
            width={16}
            loading={isProcessing}
            disabled={!email || !password || isProcessing}
            data-elm-id='submit'
          >
            Log In
          </Form.Button>
        </Form.Group>
        {this.renderOktaButton()}
        {this.renderLinks()}
      </Form>
    )
  }

  renderError = () => {
    const { error } = this.state
    return (
      <Transition.Group animation='pulse' duration={300}>
        {!!error && (
          <Message negative size='tiny' data-elm-id='error'>
            <Message.Header>{LOGIN_ERROR_HEADER}</Message.Header>
            {error}
          </Message>
        )}
        <Divider hidden fitted />
      </Transition.Group>
    )
  }

  renderHeader = () => (
    <Header as='h2' textAlign='center'>
      Sign In
    </Header>
  )

  componentWillUnmount() {
    const query = querystring.parse(window.location.search)
    if (query.redirect) {
      window.location.assign(query.redirect)
    }
    if (this._messageHandler) {
      window.removeEventListener('message', this._messageHandler)
    }
    if (this._mountTimer) {
      clearTimeout(this._mountTimer)
    }
  }

  componentDidMount() {
    const { cookies } = this.props
    const email = cookies.get(LOGIN_EMAIL) || ''
    if (email) {
      this.setState({ email, rememberEmail: true })
    }
    this._mountTimer = setTimeout(() => {
      this.setState({ isMounted: true })
    }, 100) // delay to avoid browser autocomplete (set input autocomplete not work)

    // Listen for auth code from the embed popup flow
    if (
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/embed/')
    ) {
      this._messageHandler = event => {
        if (event.origin !== window.location.origin) return
        if (event.data?.type !== 'morpheus-embed-auth') return
        const { code, returnTo } = event.data
        if (code) {
          window.location = `/session-handoff?code=${code}&redirect-to=${encodeURIComponent(
            returnTo
          )}`
        }
      }
      window.addEventListener('message', this._messageHandler)
    }
  }

  /* eslint-disable react/jsx-pascal-case */
  render() {
    return (
      <div className={cx('container')}>
        <div className={cx('mb-4')}>
          <Image src={logo} style={{ width: '250px' }} />
        </div>
        <div className={cx('card')}>
          {this.renderHeader()}
          {this.renderError()}
          {this.renderForm()}
        </div>
        <div className={cx('footer', 'bold')}>
          Powering
          <LogoImage.ADC_INLINE_MONO_LIGHT />
        </div>
      </div>
    )
  }
  /* eslint-enable react/jsx-pascal-case */
}

const mapDispatchToProps = dispatch => ({
  updateAuthenticatedUser: user => dispatch(actions.user.initUser(user)),
})

export default connect(
  () => ({}),
  mapDispatchToProps
)(withCookies(LoginContainer))
